import type {
  AccuratePostingDetailItem,
  AccuratePostingSerialRow,
  AccuratePostingTransaction,
} from "../types/accuratePosting";

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asText = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const asQuantity = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;

const buildEmptySerialRows = (
  serialNumberType: string | undefined,
  quantity: number,
): AccuratePostingSerialRow[] => {
  if (serialNumberType === "UNIQUE") {
    return Array.from({ length: Math.floor(quantity) }, () => ({
      serialNumberNo: "",
      quantity: 1,
    }));
  }

  if (serialNumberType === "BATCH" && quantity > 0) {
    return [{ serialNumberNo: "", quantity }];
  }

  return [];
};

const normalizeUniqueSerialRows = (
  rows: AccuratePostingSerialRow[],
  quantity: number,
): AccuratePostingSerialRow[] => {
  const expectedRows = Math.floor(quantity);
  return Array.from({ length: expectedRows }, (_, index) => ({
    ...rows[index],
    serialNumberNo: rows[index]?.serialNumberNo || "",
    quantity: 1,
    isExisting: Boolean(rows[index]?.serialNumberNo?.trim()),
  }));
};

const markExistingSerialRows = (
  rows: AccuratePostingSerialRow[],
): AccuratePostingSerialRow[] =>
  rows.map((row) => ({
    ...row,
    isExisting: Boolean(row.serialNumberNo?.trim()),
  }));

export const normalizeAccuratePostingTransaction = (
  transaction: AccuratePostingTransaction,
  requestedModuleType: string,
): AccuratePostingTransaction => {
  const transactionRecord = asRecord(transaction);
  const vendor = asRecord(transactionRecord.vendor);
  const jobOrder = asRecord(transactionRecord.jobOrder);
  const workOrder = asRecord(transactionRecord.workOrder);

  return {
    ...transaction,
    moduleType: transaction.moduleType || requestedModuleType,
    vendorNo: transaction.vendorNo || asText(vendor.vendorNo),
    jobOrderNumber:
      transaction.jobOrderNumber || asText(jobOrder.number) || asText(workOrder.number),
    detailItem: transaction.detailItem.map((detailItem) => {
      const detailRecord = asRecord(detailItem);
      const item = asRecord(detailRecord.item);
      const itemNo = detailItem.itemNo || asText(item.no);
      const manageSN = typeof item.manageSN === "boolean" ? item.manageSN : undefined;
      const serialNumberType =
        detailItem.serialNumberType || asText(item.serialNumberType);
      const quantity = asQuantity(detailItem.quantity);
      const serialRows = Array.isArray(detailItem.detailSerialNumber)
        ? detailItem.detailSerialNumber
        : [];
      const normalizedSerialRows =
        serialNumberType === "UNIQUE"
          ? normalizeUniqueSerialRows(serialRows, quantity)
          : markExistingSerialRows(serialRows);

      return {
        ...detailItem,
        itemNo,
        manageSN,
        serialNumberType,
        detailSerialNumber:
          manageSN === false
            ? markExistingSerialRows(serialRows)
            : normalizedSerialRows.length > 0
            ? normalizedSerialRows
            : buildEmptySerialRows(serialNumberType, quantity),
      } as AccuratePostingDetailItem;
    }),
  };
};
